import React, { useState, useEffect, useMemo } from "react";
import { Popper, Paper, ClickAwayListener } from '@mui/material';
import { Box } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { InputFilterMultiCheckbox } from 'src/mtska-frontend-app-component';
import JSONPretty from 'react-json-pretty';
import { useSettingContextInComponent, useSettingsContext } from "src/mtska-frontend-app-scaffolding";
import FiltersPanel from "src/mtska-frontend-app-component/components/List/FiltersPanel";
import { getColumnDefinition } from 'src/mtska-frontend-app-component/components/List/list.functions.jsx'
import { useDataProvider } from "src/mtska-frontend-data-provider";

const Filters = ({ dataProviderName, filters: defaultFilters, style }) => {
    const { t } = useTranslation();
    const { filterModel, drawable } = useSettingContextInComponent(dataProviderName, defaultFilters);
    const { settings, settingsLoaded, addFilter } = useSettingsContext(dataProviderName, defaultFilters);
    const [filterPanelVisible, setFilterPanelVisible] = useState(false);
    const { item: config } = useDataProvider('configs/config');

    const [yearValue, setYearValue] = useState(null);
    const [legalEntityValue, setLegalEntityValue] = useState(null);
    const [areaValue, setAreaValue] = useState(null);
    const [sourceValue, setSourceValue] = useState(null);

    const areaSourceYearStaticOptions = useMemo(
        () => [
            { year: '2026', label: t('2026') },
            { year: '2025', label: t('2025') },
            { year: '2024', label: t('2024') },
        ],
        [t],
    );

    const areaSourceLegalEntityStaticOptions = useMemo(
        () => [
            { name: 'Elletre', label: t('Elletre') },
            { name: 'Levante', label: t('Levante') },
            { name: 'QuattroK', label: t('QuattroK') },
        ],
        [t],
    );

    useEffect(() => {
        if (settingsLoaded === false) return;

        const yearItem = filterModel?.items?.find(f => f.field === 'year');
        const yearRaw = yearItem?.value;
        if (yearRaw == null || yearRaw === '') {
            setYearValue(null);
        } else {
            const names =
                yearItem.operator === 'in' && Array.isArray(yearRaw) ? yearRaw : [yearRaw];
            const list = names.filter((n) => n != null && n !== '').map((n) => String(n));
            setYearValue(list.length ? { year: list } : null);
        }

        const leItem = filterModel?.items?.find(f => f.field === '_related_legalEntity_name');
        const leRaw = leItem?.value;
        if (leRaw == null || leRaw === '') {
            setLegalEntityValue(null);
        } else {
            const names =
                leItem.operator === 'in' && Array.isArray(leRaw) ? leRaw : [leRaw];
            const list = names.filter((n) => n != null && n !== '');
            setLegalEntityValue(list.length ? { name: list } : null);
        }

        const areaItem = filterModel?.items?.find(f => f.field === '_related_area_name');
        const areaRaw = areaItem?.value;
        if (areaRaw == null || areaRaw === '') {
            setAreaValue(null);
        } else {
            const names =
                areaItem.operator === 'in' && Array.isArray(areaRaw) ? areaRaw : [areaRaw];
            const list = names.filter((n) => n != null && n !== '');
            setAreaValue(list.length ? { name: list } : null);
        }

        const sourceItem = filterModel?.items?.find(f => f.field === '_related_source_name');
        const sourceRaw = sourceItem?.value;
        if (sourceRaw == null || sourceRaw === '') {
            setSourceValue(null);
        } else {
            const names =
                sourceItem.operator === 'in' && Array.isArray(sourceRaw) ? sourceRaw : [sourceRaw];
            const list = names.filter((n) => n != null && n !== '');
            setSourceValue(list.length ? { name: list } : null);
        }
    }, [filterModel, settingsLoaded]);

    return (drawable &&
        <>
            {/* filterModel in Filters
            <JSONPretty data={filterModel} className="json-wrapper" style={{ overflow: 'scroll', fontSize: '10px' }}></JSONPretty> */}

            <Box className="section-filters" sx={style}>

                <InputFilterMultiCheckbox
                    staticOptions={areaSourceYearStaticOptions}
                    valueKey="year"
                    labelkey="label"
                    value={yearValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('year', null);
                        } else {
                            const names = Array.isArray(v.year) ? v.year : [v.year];
                            addFilter('year', names, 'in');
                        }
                    }}
                    label={t('year')}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    staticOptions={areaSourceLegalEntityStaticOptions}
                    valueKey="name"
                    labelkey="label"
                    value={legalEntityValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_legalEntity_name', null);
                        } else {
                            const names = Array.isArray(v.name) ? v.name : [v.name];
                            addFilter('_related_legalEntity_name', names, 'in');
                        }
                    }}
                    label={t('legalEntity_name')}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    dataProvider="commercials/area"
                    valueKey="name"
                    value={areaValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_area_name', null);
                        } else {
                            const names = Array.isArray(v.name) ? v.name : [v.name];
                            addFilter('_related_area_name', names, 'in');
                        }
                    }}
                    label={t('area')}
                    labelkey="name"
                    project={['name']}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    dataProvider="advertisings/source"
                    valueKey="name"
                    value={sourceValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_source_name', null);
                        } else {
                            const names = Array.isArray(v.name) ? v.name : [v.name];
                            addFilter('_related_source_name', names, 'in');
                        }
                    }}
                    label={t('source')}
                    labelkey="name"
                    project={['name']}
                    style={{ flex: '1' }}
                    size="small"
                />

                {/* per ora disabilitiamo il bottone di apertura dei filtri
                <Button onClick={(e) => { e.stopPropagation(); setFilterPanelVisible(!filterPanelVisible)}} icon="faEllipsisVertical"></Button> 
                */}
            </Box>

            <ClickAwayListener onClickAway={() => setFilterPanelVisible(false)}>
                <Popper open={filterPanelVisible}>
                    <Paper>
                        <FiltersPanel dataProviderName={dataProviderName} columns={getColumnDefinition(settings, config, dataProviderName, t)} />
                    </Paper>
                </Popper>
            </ClickAwayListener>
        </>
    )
}

export default Filters;
